(() => {
  if (customElements.get('cart-benefits')) return;

  class CartBenefits extends HTMLElement {
    constructor() {
      super();
      this.isProcessing = false;
      this.eventsBound = false;
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      if (!this.eventsBound) {
        this.bindEvents();
        this.eventsBound = true;
      }
      this.validateGiftEligibility();
    }

    bindEvents() {
      this.addEventListener('click', (event) => {
        const openButton = event.target.closest('[data-open-gift-modal]');
        if (openButton) {
          event.preventDefault();
          if (!openButton.hasAttribute('disabled')) this.openModal();
          return;
        }

        if (event.target.closest('[data-close-gift-modal]')) {
          event.preventDefault();
          this.closeModal();
          return;
        }

        const detailButton = event.target.closest('[data-toggle-gift-details]');
        if (detailButton) {
          event.preventDefault();
          const card = detailButton.closest('[data-gift-card]');
          if (card) card.classList.toggle('is-detail-open');
          return;
        }

        const selectButton = event.target.closest('[data-select-gift]');
        if (selectButton) {
          event.preventDefault();
          const card = selectButton.closest('[data-gift-card]');
          if (card) this.selectGift(card);
          return;
        }

        const giftCard = event.target.closest('[data-gift-card]');
        if (giftCard && !event.target.closest('button, a, select, input, textarea, label')) {
          event.preventDefault();
          this.selectGift(giftCard);
          return;
        }

        const submit = event.target.closest('[data-add-selected-gift]');
        if (submit) {
          event.preventDefault();
          this.addSelectedGift();
        }
      });

      this.addEventListener('change', (event) => {
        const select = event.target.closest('[data-gift-variant-select]');
        if (!select) return;
        const card = select.closest('[data-gift-card]');
        if (!card) return;
        card.dataset.giftVariantId = select.value;
        this.selectGift(card);
      });
    }

    get modal() {
      return this.querySelector('[data-gift-modal]');
    }

    get selectedCard() {
      return this.querySelector('[data-gift-card].is-selected');
    }

    get firstGiftCard() {
      return this.querySelector('[data-gift-card]');
    }

    getFetchConfig() {
      if (typeof fetchConfig === 'function') return fetchConfig();
      return {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      };
    }

    isCartPage() {
      const route = (window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart').replace(/\/$/, '');
      const path = window.location.pathname.replace(/\/$/, '');

      return (
        document.body.classList.contains('template-cart') ||
        Boolean(document.querySelector('cart-items.cart-page, .cart-page cart-items, #main-cart-items')) ||
        path === route ||
        path.endsWith('/cart')
      );
    }

    openModal() {
      const modal = this.modal;
      if (!modal) return;

      if (!this.selectedCard && this.firstGiftCard) {
        this.selectGift(this.firstGiftCard);
      }

      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('celina-gift-modal-open');
      document.addEventListener('keydown', this.onKeydown);

      const firstSelectable = this.querySelector('[data-gift-card].is-selected [data-select-gift]') || this.querySelector('[data-gift-card] [data-select-gift]');
      const close = this.querySelector('[data-close-gift-modal]');
      setTimeout(() => (firstSelectable || close)?.focus?.(), 0);
    }

    closeModal() {
      const modal = this.modal;
      if (!modal) return;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('celina-gift-modal-open');
      document.removeEventListener('keydown', this.onKeydown);
    }

    onKeydown(event) {
      if (event.key === 'Escape') this.closeModal();
    }

    selectGift(card) {
      this.classList.remove('is-gift-required');
      this.querySelectorAll('[data-gift-card]').forEach((item) => {
        item.classList.toggle('is-selected', item === card);
      });
      const counter = this.querySelector('[data-selected-gift-count]');
      if (counter) counter.textContent = '1';
      const submit = this.querySelector('[data-add-selected-gift]');
      if (submit) submit.removeAttribute('disabled');
    }

    setLoading(state) {
      this.isProcessing = state;
      this.classList.toggle('is-loading', state);
    }

    async fetchCart() {
      const response = await fetch(`${window.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Cart request failed');
      return response.json();
    }

    getConfiguredGiftVariantIds() {
      return new Set(
        String(this.dataset.giftVariantIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean),
      );
    }

    isGiftCartItem(item) {
      const properties = item.properties || {};
      const isMarkedGift = properties._celina_gift === 'true' || properties._celina_gift === true;
      const variantId = item.variant_id || item.id;
      const isConfiguredGiftVariant = variantId && this.getConfiguredGiftVariantIds().has(String(variantId));

      return isMarkedGift || isConfiguredGiftVariant;
    }

    getGiftItems(cart) {
      return (cart.items || []).filter((item) => this.isGiftCartItem(item));
    }

    getPaidSubtotal(cart) {
      return (cart.items || []).reduce((total, item) => {
        return this.isGiftCartItem(item) ? total : total + (item.final_line_price || 0);
      }, 0);
    }

    async removeGiftItems(cart) {
      const giftItems = this.getGiftItems(cart);
      const removedKeys = new Set();

      for (const item of giftItems) {
        if (!item.key || removedKeys.has(item.key)) continue;
        removedKeys.add(item.key);

        await fetch(window.routes.cart_change_url, {
          ...this.getFetchConfig(),
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
      }
    }

    async addGiftVariant(variantId) {
      const response = await fetch(window.routes.cart_add_url, {
        ...this.getFetchConfig(),
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
          properties: {
            _celina_gift: 'true',
          },
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Não foi possível adicionar o brinde.');
      }
    }

    async normalizeGiftItems(cart) {
      const giftItems = this.getGiftItems(cart);
      if (!giftItems.length) return false;

      const needsNormalization = giftItems.length > 1 || giftItems.some((item) => Number(item.quantity || 0) > 1);
      if (!needsNormalization) return false;

      const variantToKeep = giftItems[0].variant_id || giftItems[0].id;
      await this.removeGiftItems(cart);

      if (variantToKeep) {
        await this.addGiftVariant(variantToKeep);
      }

      return true;
    }

    async validateGiftEligibility() {
      if (this.dataset.giftEnabled !== 'true') return;
      const threshold = Number(this.dataset.giftThreshold || 0);
      if (!threshold) return;

      try {
        const cart = await this.fetchCart();
        const paidSubtotal = this.getPaidSubtotal(cart);
        const giftItems = this.getGiftItems(cart);

        if (giftItems.length && paidSubtotal < threshold) {
          await this.removeGiftItems(cart);
          this.refreshCartUI();
          return;
        }

        if (giftItems.length) {
          const didNormalize = await this.normalizeGiftItems(cart);
          if (didNormalize) this.refreshCartUI();
        }
      } catch (error) {
        console.warn('[Celina cart benefits] Gift eligibility check failed', error);
      }
    }

    async addSelectedGift() {
      if (this.isProcessing || window.CelinaCartGiftRequestInProgress) return;
      let card = this.selectedCard;
      if (!card && this.firstGiftCard) {
        this.selectGift(this.firstGiftCard);
        card = this.selectedCard;
      }
      if (!card) return;

      const variantId = card.dataset.giftVariantId;
      if (!variantId) return;

      window.CelinaCartGiftRequestInProgress = true;
      this.setLoading(true);

      try {
        const cart = await this.fetchCart();
        await this.removeGiftItems(cart);
        await this.addGiftVariant(variantId);

        this.closeModal();
        this.refreshCartUI();
      } catch (error) {
        console.warn('[Celina cart benefits] Add gift failed', error);
        alert(error.message || 'Não foi possível adicionar o brinde. Tente novamente.');
      } finally {
        this.setLoading(false);
        window.CelinaCartGiftRequestInProgress = false;
      }
    }

    refreshCartUI() {
      if (this.isCartPage()) {
        window.location.reload();
        return;
      }

      document.dispatchEvent(new CustomEvent('cart-drawer:refresh', { bubbles: true }));

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-benefits' });
      }
    }
  }

  customElements.define('cart-benefits', CartBenefits);

  function celinaCartBenefitsGetInstances() {
    return Array.prototype.slice.call(document.querySelectorAll('cart-benefits'));
  }

  function celinaCartBenefitsGiftSelectionRequired(instance) {
    if (!instance) return false;
    return (
      instance.dataset.giftEnabled === 'true' &&
      instance.dataset.giftUnlocked === 'true' &&
      instance.dataset.hasGift !== 'true' &&
      instance.dataset.giftConfigured === 'true'
    );
  }

  function celinaCartBenefitsGetRequiredInstance() {
    const instances = celinaCartBenefitsGetInstances().filter(celinaCartBenefitsGiftSelectionRequired);
    if (!instances.length) return null;

    const visibleInstance = instances.find((instance) => {
      const rect = instance.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });

    return visibleInstance || instances[0];
  }

  function celinaCartBenefitsIsCheckoutTrigger(element) {
    if (!element || !element.closest) return false;
    return Boolean(
      element.closest('[name="checkout"], button[type="submit"][form="cart"], button[type="submit"][form="CartDrawer-Form"], a[href*="/checkout"], .shopify-payment-button__button, .additional-checkout-buttons button, .additional-checkout-buttons a'),
    );
  }

  function celinaCartBenefitsHandleRequiredGift(event) {
    const instance = celinaCartBenefitsGetRequiredInstance();
    if (!instance) return true;

    if (event) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    }

    instance.classList.add('is-gift-required');
    const status = instance.querySelector('.celina-cart-benefits__gift-status');
    if (status && typeof status.scrollIntoView === 'function') {
      status.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    if (typeof instance.openModal === 'function') {
      instance.openModal();
    } else {
      instance.querySelector('[data-open-gift-modal]')?.click?.();
    }

    return false;
  }

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form || (form.id !== 'cart' && form.id !== 'CartDrawer-Form')) return;

    const submitter = event.submitter || document.activeElement;
    if (submitter && submitter.name && submitter.name !== 'checkout') return;

    celinaCartBenefitsHandleRequiredGift(event);
  }, true);

  document.addEventListener('click', (event) => {
    if (!celinaCartBenefitsIsCheckoutTrigger(event.target)) return;
    celinaCartBenefitsHandleRequiredGift(event);
  }, true);

  function celinaCartBenefitsFetchConfig() {
    if (typeof fetchConfig === 'function') return fetchConfig();
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    };
  }

  function celinaCartBenefitsIsCartPage() {
    const route = (window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart').replace(/\/$/, '');
    const path = window.location.pathname.replace(/\/$/, '');
    return document.body.classList.contains('template-cart') || Boolean(document.querySelector('cart-items.cart-page, #main-cart-items')) || path === route || path.endsWith('/cart');
  }

  function celinaCartBenefitsRefreshCart() {
    if (celinaCartBenefitsIsCartPage()) {
      window.location.reload();
      return;
    }

    document.dispatchEvent(new CustomEvent('cart-drawer:refresh', { bubbles: true }));

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-cross-sell' });
    }
  }

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-cart-cross-sell-add]');
    if (!button) return;

    event.preventDefault();
    if (button.disabled || button.classList.contains('is-loading')) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(window.routes.cart_add_url, {
        ...celinaCartBenefitsFetchConfig(),
        body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Não foi possível adicionar este produto.');
      }

      celinaCartBenefitsRefreshCart();
    } catch (error) {
      console.warn('[Celina cart cross-sell] Add failed', error);
      alert(error.message || 'Não foi possível adicionar este produto. Tente novamente.');
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  });

})();
